import Highcharts from 'highcharts'
import 'highcharts/modules/exporting'
import 'highcharts/modules/offline-exporting'
import moment from 'moment'

const GraphTypes = {
  AreaRange: 'arearange',
  Scatter: 'scatter',
  Column: 'column',
  Windrose: 'column',
  Line: 'line',
}

const CUBIC_METRE_PER_SECOND = 'm³/s'
const HISTORICAL_RANGE = 'Historical Range'

function defaultTooltipFormatter() {
  return '<b>' + this.series.name + '</b><br/>' +
    Highcharts.dateFormat('%e. %b. %Y, %H:%M', this.x) + ': ' + this.y + ' ' + this.series.options.unit +
    ((this.series.options.dataQaQc && this.series.options.dataQaQc.length > this.point.index) ?
      '<br/>' + 'QA/QC Level: ' + this.series.options.dataQaQc[this.point.index] : '') +
    ((this.series.options.dataGrades && this.series.options.dataGrades.length > this.point.index) ?
      '<br/>' + 'Data Grade: ' + this.series.options.dataGrades[this.point.index] : '') +
    ((this.series.options.dataQualifiers && this.series.options.dataQualifiers.length > this.point.index) ?
      '<br/>' + 'RISC Grade: ' + this.series.options.dataQualifiers[this.point.index] : '')
}

function makeExportingConfig() {
  return {
    sourceWidth: 1000,
    sourceHeight: 800,
    chartOptions: {
      subtitle: {
        text: '',
      },
      legend: {
        useHTML: false,
        itemStyle: {
          width: 200,
        },
        labelFormatter: function () {
          var maxLineLength = 28
          var name = this.name
          if (name.length <= maxLineLength) return name
          var words = name.split(' ')
          var lines = []
          var currentLine = ''
          for (var i = 0; i < words.length; i++) {
            if (currentLine && (currentLine + ' ' + words[i]).length > maxLineLength) {
              lines.push(currentLine)
              currentLine = words[i]
            } else {
              currentLine = currentLine ? currentLine + ' ' + words[i] : words[i]
            }
          }
          lines.push(currentLine)
          return lines.join('<br/>')
        },
      },
    },
    buttons: {
      autoScaleButton: {
        x: -40,
        text: 'Auto Scale',
        onclick: function () {
          var chart = this
          var seriesData = chart.series.map(function (s) { return s.options })
          var globalYAxisMaxPerUnit = computeMaxForVisibleSeriesInChart(seriesData, chart)
          var globalYAxisMinPerUnit = computeMinForVisibleSeriesInChart(seriesData, chart)
          setExtremesForVisibleSeriesInChart(seriesData, globalYAxisMaxPerUnit, globalYAxisMinPerUnit, chart)
          chart.showResetZoom()
        },
        symbol: 'url(https://api.iconify.design/ic:baseline-zoom-out-map.svg)',
        symbolY: 16,
        symbolX: 22,
        symbolSize: 6,
        className: 'highcharts-autoscale-button',
        theme: {
          fill: 'white',
          stroke: 'silver',
        },
      },
    },
  }
}

function wrapTitleText(text, maxLineLength) {
  if (typeof text !== 'string' || !text) {
    return { text: text, wrapped: false }
  }
  const words = text.split(' ')
  if (words.length <= 1) {
    return { text: text, wrapped: false }
  }
  const lines = []
  let current = ''
  words.forEach(word => {
    if (current && (current + ' ' + word).length > maxLineLength) {
      lines.push(current)
      current = word
    } else {
      current = current ? current + ' ' + word : word
    }
  })
  if (current) lines.push(current)
  return { text: lines.join('<br/>'), wrapped: lines.length > 1 }
}

function makeChartConfig(renderTo, args, data, tooltipOption) {
  const titleLines = wrapTitleText(args.title, 40)
  const titleAlign = typeof args.title === 'string' && args.title.length > 28 ? 'left' : 'center'
  var config = {
    chart: {
      renderTo: renderTo,
      zoomType: 'xy',
      panning: true,
      panKey: 'shift',
      resetZoomButton: {
        position: {
          align: 'right',
          verticalAlign: 'top',
          x: -160,
        },
        relativeTo: 'chart',
        theme: {
          fill: 'white',
          stroke: 'silver',
          padding: 5,
        },
      },
    },
    loading: {
      hideDuration: 1,
      showDuration: 1,
    },
    credits: {
      enabled: false,
    },
    legend: {
      enabled: args.showLegend !== false,
      useHTML: true,
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      labelFormatter: function () {
        return '<span style="display: inline-block; max-width: 180px; overflow-wrap: break-word; word-wrap: break-word; white-space: normal; vertical-align: middle;">' + this.name + '</span>'
      },
    },
    title: {
      text: titleLines.text,
      align: titleAlign,
      style: {
        fontSize: '14px',
      },
    },
    subtitle: {
      text: 'Click and drag to zoom in. Hold down shift key to pan.',
    },
    xAxis: {
      type: 'datetime',
      dateTimeLabelFormats: {
        day: '%e. %b',
        month: '%b. %Y',
        year: '%Y',
      },
    },
    yAxis: args.yAxis,
    plotOptions: {
      scatter: {
        marker: {
          enabled: true,
          radius: 4,
        },
        zIndex: 3,
      },
      line: {
        marker: {
          enabled: true,
          radius: 2,
        },
        zIndex: 2,
      },
      arearange: {
        zIndex: 1,
        stickyTracking: false,
      },
      series: {
        events: {
          hide: function () {
            var chart = this.chart
            var seriesData = chart.series.map(function (s) { return s.options })
            var globalYAxisMaxPerUnit = computeMaxForVisibleSeriesInChart(seriesData, chart)
            setExtremesForVisibleSeriesInChart(seriesData, globalYAxisMaxPerUnit, null, chart)
          },
          show: function () {
            var chart = this.chart
            var seriesData = chart.series.map(function (s) { return s.options })
            var globalYAxisMaxPerUnit = computeMaxForVisibleSeriesInChart(seriesData, chart)
            setExtremesForVisibleSeriesInChart(seriesData, globalYAxisMaxPerUnit, null, chart)
          },
          legendItemClick: function () {
            this.chart.showLoading()
            setTimeout(() => {
              var seriesToUpdate = this.chart.series.filter(x => x.name === this.name)[0]
              seriesToUpdate && seriesToUpdate.setVisible(!this.visible, false)
            }, 100)
            return false
          },
        },
      },
    },
    series: data,
    exporting: makeExportingConfig(),
    responsive: {
      rules: [{
        condition: {
          maxWidth: 500,
        },
        chartOptions: {
          legend: {
            align: 'center',
            verticalAlign: 'bottom',
            layout: 'horizontal',
            labelFormatter: function () {
              return '<span style="display: inline-block; max-width: 120px; overflow-wrap: break-word; word-wrap: break-word; white-space: normal; vertical-align: middle; font-size: 10px;">' + this.name + '</span>'
            },
          },
        },
      }],
    },
  }

  if (tooltipOption !== undefined) {
    config.tooltip = tooltipOption
  }

  return config
}

function computeMaxForVisibleSeriesInChart(data, chart) {
  if (data && data.length && data.filter(x => x.type === GraphTypes.AreaRange).length > 0) {
    chart.redraw()
    chart.hideLoading()
    return
  }
  var globalYAxisMaxPerUnit = {}
  var dataSeries = null
  for (var i = 0; i < chart.series.length; i++) {
    dataSeries = data.filter(x => x.name == chart.series[i].name)[0]
    if (!dataSeries) continue
    if (!globalYAxisMaxPerUnit[dataSeries.unit]) {
      globalYAxisMaxPerUnit[dataSeries.unit] = 0
    }
    if (!chart.series[i].visible) {
      continue
    }
    globalYAxisMaxPerUnit[dataSeries.unit] = Math.max(globalYAxisMaxPerUnit[dataSeries.unit], chart.series[i].dataMax)
  }
  return globalYAxisMaxPerUnit
}

function computeMinForVisibleSeriesInChart(data, chart) {
  if (data && data.length && data.filter(x => x.type === GraphTypes.AreaRange).length > 0) {
    chart.redraw()
    chart.hideLoading()
    return
  }
  var globalYAxisMinPerUnit = {}
  var dataSeries = null
  for (var i = 0; i < chart.series.length; i++) {
    dataSeries = data.filter(x => x.name == chart.series[i].name)[0]
    if (!dataSeries) continue
    if (!globalYAxisMinPerUnit[dataSeries.unit]) {
      if (!Number.MAX_SAFE_INTEGER) {
        Number.MAX_SAFE_INTEGER = 9007199254740991
      }
      globalYAxisMinPerUnit[dataSeries.unit] = Number.MAX_SAFE_INTEGER
    }
    if (!chart.series[i].visible) {
      continue
    }
    globalYAxisMinPerUnit[dataSeries.unit] = Math.min(globalYAxisMinPerUnit[dataSeries.unit], chart.series[i].dataMin)
  }
  return globalYAxisMinPerUnit
}

function setExtremesForVisibleSeriesInChart(data, globalYAxisMaxPerUnit, globalYAxisMinPerUnit, chart) {
  if (globalYAxisMaxPerUnit == null && globalYAxisMinPerUnit == null) {
    return
  }
  var dataSeries = null
  for (var j = 0; j < chart.yAxis.length; j++) {
    for (var k = 0; k < chart.yAxis[j].series.length; k++) {
      dataSeries = data.filter(x => x.name == chart.yAxis[j].series[k].name)[0]
      if (dataSeries) {
        chart.yAxis[j].update({
          visible: chart.yAxis[j].series[k].visible,
        }, false, false, false)
        break
      }
    }
    if (dataSeries) {
      if (globalYAxisMaxPerUnit == null) {
        chart.yAxis[j].setExtremes(globalYAxisMinPerUnit[dataSeries.unit], null, false, false)
      } else if (globalYAxisMinPerUnit == null) {
        chart.yAxis[j].setExtremes(null, globalYAxisMaxPerUnit[dataSeries.unit], false, false)
      } else {
        chart.yAxis[j].setExtremes(globalYAxisMinPerUnit[dataSeries.unit], globalYAxisMaxPerUnit[dataSeries.unit], false, false)
      }
    }
  }
  chart.redraw()
  chart.hideLoading()
}

export default {
  props: ['id'],

  data() {
    return {
      highchartsOptions: null,
      chart: null,
    }
  },

  methods: {
    decideChartType(timeSeriesName) {
      if (timeSeriesName.indexOf('Field') >= 0) {
        return GraphTypes.Scatter
      }
      if (timeSeriesName.indexOf('Precipitation') >= 0 || timeSeriesName.indexOf('Rainfall') >= 0) {
        return GraphTypes.Column
      }
      if (timeSeriesName.indexOf('Wind') >= 0) {
        return GraphTypes.Windrose
      }
      return GraphTypes.Line
    },

    drawChartWithArgs(args, data, options) {
      var opts = options || {}
      var renderTo = opts.renderTo || this.id
      var tooltipOption = opts.tooltip

      // Resolve tooltip: false = Highcharts built-in, null/undefined = default custom, object = custom config
      if (tooltipOption === false) {
        tooltipOption = undefined
      } else if (!tooltipOption) {
        tooltipOption = { formatter: defaultTooltipFormatter }
      }

      this.prepareData(args, data)

      Highcharts.setOptions({
        time: {
          useUTC: false,
        },
      })

      this.highchartsOptions = makeChartConfig(renderTo, args, data, tooltipOption)
      /* eslint-disable */
      this.chart = new Highcharts.chart(this.highchartsOptions)
    },

    prepareData(args, data) {
      this.setYAxisMin(args, data)

      if (data && data.length && data.filter(x => x.type === GraphTypes.AreaRange).length === 0) {
        this.computeAndSetYAxisMaxPerUnit(args, data)
      }
    },

    setYAxisMin(args, data) {
      var globalYAxisMin = 0
      for (var j = 0; j < data.length; j++) {
        var index = data[j].yAxis
        var seriesData = data[index].data
        if (!seriesData) continue
        var yAxisMinForSeries = Math.min.apply(Math, seriesData.map(function (i) {
          return i[1]
        }))
        globalYAxisMin = Math.min(globalYAxisMin, yAxisMinForSeries)
      }
      if (args && args.yAxis && args.yAxis.length) {
        for (var i = 0; i < args.yAxis.length; i++) {
          args.yAxis[i].min = globalYAxisMin
        }
      } else if (args && args.yAxis) {
        args.yAxis.min = globalYAxisMin
      }
    },

    setYAxisTitleText(args, data) {
      for (var j = 0; j < data.length; j++) {
        var index = data[j].yAxis
        args.yAxis[index].title.text = data[index].name + (data[index].unit ? ' (' + data[index].unit + ')' : '')
      }
    },

    computeAndSetYAxisMaxPerUnit(args, data) {
      var globalYAxisMaxPerUnit = {}

      for (var j = 0; j < data.length; j++) {
        var index = data[j].yAxis

        if (!globalYAxisMaxPerUnit[data[index].unit]) {
          globalYAxisMaxPerUnit[data[index].unit] = 0
        }
        var yAxisMax = 0
        if (data[index].type === GraphTypes.AreaRange) {
          yAxisMax = Math.max.apply(Math, data[index].data.map(function (i) {
            return i[2]
          }))
        } else {
          yAxisMax = Math.max.apply(Math, data[index].data.map(function (i) {
            return i[1]
          }))
        }

        if (yAxisMax > globalYAxisMaxPerUnit[data[index].unit]) {
          globalYAxisMaxPerUnit[data[index].unit] = yAxisMax
        }
      }

      for (var jj = 0; jj < data.length; jj++) {
        var idx = data[jj].yAxis
        args.yAxis[idx].max = globalYAxisMaxPerUnit[data[idx].unit]
      }
    },

    constructAreaChart(timeSeriesData, masterDatasetConfig) {
      var data = []
      var maximumTimeSeries = timeSeriesData.find(x => x.DataSetName.indexOf('Maximum') >= 0)
      var minimumTimeSeries = timeSeriesData.find(x => x.DataSetName.indexOf('Minimum') >= 0)

      if (maximumTimeSeries != null && minimumTimeSeries != null) {
        var ranges = []
        for (var i = 0; i < maximumTimeSeries.Rows.length; i++) {
          ranges.push([moment(maximumTimeSeries.Rows[i].DateTime).valueOf(), minimumTimeSeries.Rows[i].Value,
            maximumTimeSeries.Rows[i].Value,
          ])
        }
        var areaTimeSeries = {
          name: HISTORICAL_RANGE,
          data: ranges,
          yAxis: masterDatasetConfig.yAxis,
          type: 'arearange',
          color: '#808080',
          fillOpacity: 0.3,
          zIndex: 0,
          unit: CUBIC_METRE_PER_SECOND,
          marker: {
            enabled: false,
          },
        }
        data.push(areaTimeSeries)
      }
      return data
    },

    removeElementsFromArray(array, elements) {
      for (var i = 0; i < elements.length; i++) {
        const index = array.indexOf(elements[i])
        if (index !== -1) {
          array.splice(index, 1)
        }
      }
      return array
    },

    floatGreaterThan(lhs, rhs, precision) {
      var lhsFloatPrecision = Number.parseFloat(Number.parseFloat(lhs).toFixed(precision))
      var rhsFloatPrecision = Number.parseFloat(Number.parseFloat(rhs).toFixed(precision))
      return lhsFloatPrecision > rhsFloatPrecision
    },

    getPrecisionByFloat(num, maxPrecision) {
      if (!num) return 0
      if (!isFinite(num)) return 0
      var e = 1,
        p = 0
      while (Math.round(num * e) / e !== num) {
        e *= 10
        p++
        if (p == maxPrecision) break
      }
      return p
    },

    computeMaxForVisibleSeriesInChart(data, chart) {
      return computeMaxForVisibleSeriesInChart(data, chart || this.chart)
    },

    computeMinForVisibleSeriesInChart(data, chart) {
      return computeMinForVisibleSeriesInChart(data, chart || this.chart)
    },

    setExtremesForVisibleSeriesInChart(data, globalYAxisMaxPerUnit, globalYAxisMinPerUnit, chart) {
      setExtremesForVisibleSeriesInChart(data, globalYAxisMaxPerUnit, globalYAxisMinPerUnit, chart || this.chart)
    },

    makeExportingConfig: makeExportingConfig,
  },
}
